import React, { useState, useMemo, useEffect } from 'react';
import { formatExeclNum, formatNum } from '@/pages/dashboard/utils/utils';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';
import { connect, getLocale, useIntl } from 'umi';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import moment from 'moment';
import ChurnChartCo from '../Nightingale/component/ChurnChartCo';
import { momentFormat } from '@/pages/dashboard/utils/_config/filtersTimeFormat';

const ChurnLevelDistributionCo = (props: any) => {
  const locale = getLocale();
  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl(); // 中英切换方法
  const isEN = locale === 'en-US';
  const { config: propsConfig, data: apiData, explainInfo, filterVal, apiDataKey, dataKey, changeData } = props;
  const { timeType } = filterVal;
  const { metricsKey, title: titleEn, titleCn, toolTip, tab, titleDes: titleDesConfig, defaultActive = 1, hideConfig } = propsConfig[timeType] || {};

  const [tabName, setTabName] = useState('');
  const [isEye, setIsEye] = useState(true);

  const title = isEN ? titleEn : titleCn;
  const toolTipTitle = isEN ? toolTip?.title : toolTip?.titleCn || '';
  const toolTipMetricsName = isEN ? toolTip?.metrics : toolTip?.metricsCn || '';

  const titleDes = titleDesConfig?.map((val)=> {
    const explain = getExplainVal(explainInfo, val?.rainbowKey, getLocale(), val?.explainEn, val?.explainCn);
    const content = isEN ? val.contentEn : val.contentCn;

    if(!explain && !content){
      return null;
    }

    return {
      title: isEN ? val.title : val.titleCn,
      content: val.isRainbow ? explain : content
    };
  }).filter((val : { title: string; content: string; } | null) => val !== null);

  const tabList = tab?.list?.map((v: { text: string; textCn: string; value: string | number; }) => ({
    text: isEN ? v.text : v.textCn,
    value: v.value
  })) || [];

  useEffect(()=>{
    if(tab?.value){
      setTabName(tab.value);
    }
  },[tab]);

  const data = useMemo(()=> {
    const metricsData = apiData?.[apiDataKey] || {};
    let isLoading = true;
    let isError = false;
    let isNoData = false;
    let componentData : any = [];
    if(metricsData){
      componentData = metricsKey ? metricsData?.data?.[metricsKey] : metricsData?.data || [];
      isLoading = metricsData?.loading;
      isError = metricsData?.errorMsg;
      isNoData = componentData?.ChurnLevelDisturb?.length <= 0;
    }
    return {
      componentData,
      isLoading,
      isError,
      isNoData
    };
  },[apiData, apiDataKey, metricsKey]);

  const height = data.isNoData ? 245 : 295;

  const options = useMemo(()=>{
    let opts : any = {
      chart: [] as Array<any>,
      table: {
        option: [] as Array<{}>,
        data: [] as Array<{}>,
        downloadData: [] as Array<{}>,
      },
    };

    const { AccumulateSheetHead, SpecificSheetHead, ChurnLevelDisturb } = data?.componentData || {};

    if (ChurnLevelDisturb?.length) {
      const heads =
        (tabName === 'accumulated' ? AccumulateSheetHead : SpecificSheetHead) ||
        [];

      // 表头数据
      const rightTHead: Array<{}> = [
        {
          key: 'date',
          header: formatMessage({ id: 'engagement_churn_Date' }),
          align: 'left',
          fixed: 'left',
          width: 160,
          render: (cvm) => {
            const weekDay = getWeekDay(cvm?.date);
            const isSunOrSat =
              weekDay === 'Sun' ||
              weekDay === 'Sat' ||
              weekDay === '周日' ||
              weekDay === '周六';
            return (
              <>
                <span className="table-text">{cvm?.date}</span>
                {timeType !== 'Month' && (
                  <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>
                    {weekDay}
                  </span>
                )}
              </>
            );
          },
        },
        ...heads.map((ele) => ({
          key: ele,
          header: ele,
          align: 'right',
          width: 100,
        })),
      ];

      // 表格数据
      const rightTData: Array<{}> = [];
      {
        ChurnLevelDisturb?.forEach((ele) => {
          const date = timeType === 'Month' ? momentFormat(ele.date, 'YYYY-MM') : ele.date;
          const kvs = ele.kv_list || [];
          const len = kvs.length;
          let i;
          let val;
          const temp = { date };

          heads.forEach((k) => {
            val = '';
            // eslint-disable-next-line no-plusplus
            for (i = len;i--;) {
              if (kvs[i].key === k) {
                val = kvs[i].value;
                break;
              }
            }
            temp[k] = formatNum(val, false, true);
          });

          len && rightTData.push(temp);
        });
      }
      // 下载数据
      const rightTDownloadData: Array<{}> = [];
      {
        ChurnLevelDisturb?.forEach((ele) => {
          const date = timeType === 'Month' ? momentFormat(ele.date, 'YYYY-MM') : ele.date;
          const kvs = ele.kv_list || [];
          const len = kvs.length;
          let i;
          let val;
          const temp = { date };

          heads.forEach((k) => {
            val = '';
            // eslint-disable-next-line no-plusplus
            for (i = len;i--;) {
              if (kvs[i].key === k) {
                val = kvs[i].value;
                break;
              }
            }
            temp[k] = formatExeclNum(val);
          });

          len && rightTDownloadData.push(temp);
        });
      }

      // 图表
      const rightChart: {
        name: string;
        stack: string;
        data: any[];
        barCategoryGap?: string;
      }[] = [];

      heads?.forEach((level) => {
        const data: {
          x: string;
          y: string | number;
        }[] = [];
        ChurnLevelDisturb?.forEach((listItem) => {
          const date = listItem?.date;
          const value = listItem?.[
            tabName === 'accumulated' ? 'kv_list' : 'top10'
          ]?.filter((item) => item?.key === level)?.[0]?.value;

          data.push({
            x: timeType === 'Month' ? momentFormat(date, 'YYYY-MM') : date,
            y: Math.round(value) || value,
          });
        });
        rightChart.push({
          name: level,
          stack: 'stack',
          barCategoryGap: '50%',
          data,
        });
      });
      opts = {
        chart:
        tabName === 'accumulated'
          ? rightChart
          : rightChart?.filter((item) =>
            item?.data?.some((dataItem) => dataItem?.y),
          ),
        table: {
          option: rightTHead,
          data: rightTData,
          downloadData: rightTDownloadData,
        },
      };
    }

    return opts;
  },[data, isEN]);

  const formatterFunc = (params) => {
    const data = params.map(({ seriesName, value, color }) => (
      value ?  
        {
          name: seriesName,
          metricsNum: formatNum(value, false, true),
          color,
        } : null
    )).filter(item => item !== null);
    
    if(data?.length <= 0){
      return '<div></div>';
    } 
    const metricsName = timeType === 'Month' ? moment(`${params[0].axisValue}`).format('YYYY-MM') : moment(`${params[0].axisValue}`).format('YYYY-MM-DD');
    return getEchartTooltipText({
      title: toolTipTitle,
      metricsName: toolTipMetricsName || metricsName,
      data
    });
  };

  return (
    <ChurnChartCo
      options={options}
      timeType={timeType}
      tabName={tabName}
      setTabName={(val: string)=> {
        setTabName(val);
        changeData(dataKey, val);
      }}
      tabList={tabList}
      title={title}
      titleDes={titleDes}
      isLoading={data.isLoading}
      isError={data.isError}
      isNoData={data.isNoData}
      defaultActive={defaultActive}
      formatterFunc={formatterFunc}
      // setOtherConfigFunc={setOtherConfigFunc}
      isEye={isEye}
      setIsEye={setIsEye}
      hideConfig={hideConfig}
      height={height}
    />
  );
};

export default connect((state: any) => ({
  explainInfo: state.dashboard?.explainInfo,
}))(ChurnLevelDistributionCo);